import { useContext } from "react";
import { useToggleAgentPrompt } from "services/agents";
import { AgentPrompt } from "protogen/agents_service_pb";
import TruncatedText from "components/common/TruncatedText";
import { CurrentUserContext } from "components/context/RequireAuth";
import { Switch, Chip, Box, Typography } from "@mui/material";
import ClientOutputVisibleIndicator from "components/tools/ClientOutputVisibleIndicator";

interface PlaybookCardProps {
  playbook: AgentPrompt;
  onUpdate: () => Promise<void>;
  isLast: boolean;
  isFirst: boolean;
}

export default function PlaybookCard({
  playbook,
  onUpdate,
  isLast,
  isFirst,
}: PlaybookCardProps) {
  const currentUser = useContext(CurrentUserContext);
  const { request: toggleAgentPrompt } = useToggleAgentPrompt();

  const handleToggleEnabled = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isEnabled = e.target.checked;
    const response = await toggleAgentPrompt({
      promptRef: playbook.ref,
      isEnabled: isEnabled,
    });

    if (response) {
      onUpdate();
    }
  };

  const getOwnerDisplay = () => {
    const ownerName = playbook.owner?.displayName || "";
    if (
      ownerName.toLowerCase() === "faye" ||
      ownerName.toLowerCase() === "faye admin"
    ) {
      return "Faye";
    }
    if (playbook?.owner?.ref === currentUser?.ref) {
      return "You";
    }
    return ownerName;
  };

  const getAddedDateDisplay = () => {
    const createdDate = new Date(Number(playbook.creationSec) * 1000);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - createdDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return "Added today";
    } else if (diffDays === 2) {
      return "Added yesterday";
    } else if (diffDays <= 7) {
      return `Added ${diffDays - 1} days ago`;
    } else {
      return `Added ${createdDate.toLocaleDateString()}`;
    }
  };

  const getScopeLabel = () => {
    if (playbook.scope === "system-default") return "System Default";
    if (playbook.scope === "public") return "Public";
    if (playbook.scope === "private") return "Private";
    return "";
  };

  const getScopeVariant = ():
    | "teal"
    | "red"
    | "gray"
    | "green"
    | "warning"
    | "outlined" => {
    if (playbook.scope === "system-default") return "teal";
    if (playbook.scope === "public") return "green";
    if (playbook.scope === "private") return "gray";
    return "gray";
  };

  return (
    <Box
      sx={{
        borderBottom: "1px solid #EAEBEC",
        background: "#FFF",
        padding: "16px 24px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        position: "relative",
        "&:hover": {
          backgroundColor: "#FAFAFA",
        },
        ...(isLast && {
          borderBottomRightRadius: "8px",
          borderBottomLeftRadius: "8px",
        }),
        ...(isFirst && {
          borderTopRightRadius: "8px",
          borderTopLeftRadius: "8px",
        }),
      }}
      onClick={() => {
        window.location.href = `/tools/agents/playbooks/${encodeURIComponent(
          playbook.ref,
        )}`;
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="16px"
      >
        <Box flex="1" sx={{ minWidth: 0 }}>
          <Box display="flex" alignItems="center" gap="16px" mb="4px">
            <Typography
              variant="bodyHeavy"
              sx={{
                fontSize: "16px",
                color: "#101828",
              }}
            >
              {playbook.name}
            </Typography>
            {playbook.scope && (
              <Chip
                label={getScopeLabel()}
                size="small"
                variant={getScopeVariant()}
                sx={{ height: "20px", fontSize: "12px" }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" flexDirection="row">
            <Typography
              variant="bodySmall"
              sx={{
                fontSize: "13px",
                color: "#667085",
              }}
            >
              {getOwnerDisplay()}
              {" • "}
              {getAddedDateDisplay()}
            </Typography>
          </Box>
          <Typography
            variant="body"
            sx={{
              fontSize: "14px",
              color: "#475467",
              marginBottom: "8px",
            }}
          >
            <TruncatedText lines={2}>
              {playbook.content?.content?.textContent || "No description"}
            </TruncatedText>
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="8px"
          sx={{ flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            checked={playbook.isEnabledForUser}
            onChange={handleToggleEnabled}
            sx={{ width: "unset" }}
          />
          <ClientOutputVisibleIndicator
            visible={playbook.clientOutputVisible}
            showLabel={false}
            size={20}
          />
        </Box>
      </Box>
    </Box>
  );
}
